/**
 * Lectura del GPS del dispositivo.
 *
 * Decisiones que vienen del uso real en campo:
 *
 *  - `watchPosition` y no `getCurrentPosition`: la primera lectura del GPS de
 *    un teléfono suele llegar con ±100 m o más y mejora en segundos. Pedir una
 *    sola lectura daría una precisión que §9.4 rechazaría.
 *  - Se conserva la MEJOR lectura de la ventana, no la última: el GPS oscila, y
 *    quedarse con la última puede ser peor que una anterior.
 *  - `enableHighAccuracy` consume batería, así que la vigilancia se detiene en
 *    cuanto se alcanza una precisión aceptable o se agota el tiempo. En una
 *    jornada de 6–7 entrevistas más traslados, dejarlo encendido todo el día
 *    agotaría el teléfono antes de terminar.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { QUALITY_THRESHOLDS } from '@capi/shared';

export interface GpsReading {
  latitude: number;
  longitude: number;
  accuracyMeters: number | null;
  capturedAt: string;
}

export type GpsPhase =
  | 'idle'
  /** Esperando el permiso del navegador. */
  | 'requesting'
  /** Vigilando y mejorando la precisión. */
  | 'acquiring'
  /** Precisión suficiente alcanzada. */
  | 'ready'
  /** Se agotó el tiempo con una lectura imprecisa. */
  | 'imprecise'
  | 'denied'
  | 'unavailable';

export interface GpsState {
  phase: GpsPhase;
  reading: GpsReading | null;
  /** Mejor precisión obtenida en la ventana, en metros. */
  bestAccuracy: number | null;
  error: string | null;
  /** Segundos que lleva vigilando. */
  elapsedSeconds: number;
}

const ACCEPTABLE_ACCURACY = QUALITY_THRESHOLDS.maxGpsAccuracyMeters;

/** Tiempo máximo de vigilancia antes de conformarse con lo que haya. */
const WATCH_TIMEOUT_MS = 25_000;

export function useGps(): GpsState & {
  capture: () => void;
  stop: () => void;
  reset: () => void;
} {
  const [state, setState] = useState<GpsState>({
    phase: 'idle',
    reading: null,
    bestAccuracy: null,
    error: null,
    elapsedSeconds: 0,
  });

  const watchIdRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const bestRef = useRef<GpsReading | null>(null);

  const stopWatching = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const capture = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setState({
        phase: 'unavailable',
        reading: null,
        bestAccuracy: null,
        error: 'Este dispositivo no permite obtener la ubicación.',
        elapsedSeconds: 0,
      });
      return;
    }

    stopWatching();
    bestRef.current = null;
    const startedAt = Date.now();
    setState({
      phase: 'requesting',
      reading: null,
      bestAccuracy: null,
      error: null,
      elapsedSeconds: 0,
    });

    tickRef.current = window.setInterval(() => {
      setState((prev) => ({
        ...prev,
        elapsedSeconds: Math.round((Date.now() - startedAt) / 1000),
      }));
    }, 1000);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const accuracy = Number.isFinite(position.coords.accuracy)
          ? position.coords.accuracy
          : null;
        const reading: GpsReading = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMeters: accuracy,
          capturedAt: new Date(position.timestamp).toISOString(),
        };

        // Se conserva la mejor lectura, no la última.
        const previousBest = bestRef.current;
        const isBetter =
          previousBest === null ||
          (accuracy !== null &&
            (previousBest.accuracyMeters === null || accuracy < previousBest.accuracyMeters));
        if (isBetter) bestRef.current = reading;

        const best = bestRef.current!;
        const bestAccuracy = best.accuracyMeters;
        const goodEnough = bestAccuracy !== null && bestAccuracy <= ACCEPTABLE_ACCURACY;

        setState((prev) => ({
          ...prev,
          phase: goodEnough ? 'ready' : 'acquiring',
          reading: best,
          bestAccuracy,
          error: null,
        }));

        if (goodEnough) stopWatching();
      },
      (error) => {
        stopWatching();
        const messages: Record<number, { phase: GpsPhase; message: string }> = {
          1: {
            phase: 'denied',
            message:
              'Falta el permiso de ubicación. Actívalo en los ajustes del navegador: §9.4 exige GPS en cada captura.',
          },
          2: {
            phase: 'unavailable',
            message:
              'No se pudo obtener la ubicación. Sal al aire libre, lejos de muros y techos, y reintenta.',
          },
          3: {
            phase: 'imprecise',
            message: 'La búsqueda de señal tardó demasiado. Reintenta al aire libre.',
          },
        };
        const mapped = messages[error.code] ?? {
          phase: 'unavailable' as GpsPhase,
          message: 'No se pudo obtener la ubicación.',
        };
        setState((prev) => ({
          ...prev,
          phase: bestRef.current ? 'imprecise' : mapped.phase,
          reading: bestRef.current,
          error: mapped.message,
        }));
      },
      { enableHighAccuracy: true, timeout: WATCH_TIMEOUT_MS, maximumAge: 0 },
    );

    timeoutRef.current = window.setTimeout(() => {
      stopWatching();
      setState((prev) => ({
        ...prev,
        phase: bestRef.current ? 'imprecise' : 'unavailable',
        reading: bestRef.current,
        error: bestRef.current
          ? null
          : 'No se obtuvo ninguna lectura. Sal al aire libre y reintenta.',
      }));
    }, WATCH_TIMEOUT_MS);
  }, [stopWatching]);

  const reset = useCallback(() => {
    stopWatching();
    bestRef.current = null;
    setState({
      phase: 'idle',
      reading: null,
      bestAccuracy: null,
      error: null,
      elapsedSeconds: 0,
    });
  }, [stopWatching]);

  useEffect(() => stopWatching, [stopWatching]);

  return { ...state, capture, stop: stopWatching, reset };
}

/** Vigilancia continua para el mapa, con menos exigencia de precisión. */
export function useLivePosition(enabled: boolean): GpsReading | null {
  const [position, setPosition] = useState<GpsReading | null>(null);

  useEffect(() => {
    if (!enabled || !('geolocation' in navigator)) return;
    const id = navigator.geolocation.watchPosition(
      (p) => {
        setPosition({
          latitude: p.coords.latitude,
          longitude: p.coords.longitude,
          accuracyMeters: Number.isFinite(p.coords.accuracy) ? p.coords.accuracy : null,
          capturedAt: new Date(p.timestamp).toISOString(),
        });
      },
      () => undefined,
      // Alta precisión apagada: para situarse en el mapa basta con menos, y
      // encenderla durante todo el recorrido agotaría la batería.
      { enableHighAccuracy: false, timeout: 20_000, maximumAge: 10_000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [enabled]);

  return position;
}

/** Texto legible de la precisión, para mostrar al encuestador. */
export function formatAccuracy(accuracyMeters: number | null): string {
  if (accuracyMeters === null) return 'precisión desconocida';
  return `±${Math.round(accuracyMeters)} m`;
}

export function accuracyQuality(accuracyMeters: number | null): 'good' | 'fair' | 'poor' {
  if (accuracyMeters === null) return 'poor';
  if (accuracyMeters <= ACCEPTABLE_ACCURACY) return 'good';
  if (accuracyMeters <= ACCEPTABLE_ACCURACY * 2) return 'fair';
  return 'poor';
}
